import { z } from "zod";
import { MAX_LOT_CODE_LENGTH, MAX_STOCK_QTY_Q4, type LotStatus } from "@cx/domain";
import { listQuery } from "./common";
import { blank } from "./fields";
import { ulidParam } from "./ids";
import { businessDate } from "./prices";

// ===== Lotes (RF-SUP-07, T3.2) =====

const optDate = blank.pipe(businessDate.nullable());

const datesOrder = (v: { manufacturedAt: string | null; expiresAt: string | null }, ctx: z.RefinementCtx) => {
  if (v.manufacturedAt && v.expiresAt && v.expiresAt < v.manufacturedAt)
    ctx.addIssue({
      code: "custom",
      path: ["expiresAt"],
      message: "A validade não pode ser anterior à fabricação.",
    });
};

/** Datas do lote (opcionais, correção do cadastro do lote): validade não anterior à fabricação. */
export const lotDates = z.object({ manufacturedAt: optDate, expiresAt: optDate }).superRefine(datesOrder);
export type LotDatesInput = z.output<typeof lotDates>;

/** Lote de uma entrada: código (até 20, o nLote da NF-e) e datas. O lote existente do produto é reaproveitado. */
export const lotInput = z
  .object({
    code: z
      .string("Informe o lote")
      .trim()
      .min(1, "Informe o lote")
      .max(MAX_LOT_CODE_LENGTH, `Até ${MAX_LOT_CODE_LENGTH} caracteres`),
    manufacturedAt: optDate,
    expiresAt: optDate,
  })
  .superRefine(datesOrder);
export type LotInput = z.output<typeof lotInput>;

/** Lote de uma entrada com quantidade (recebimento da compra, nota de entrada). */
export const lotQtyInput = lotInput.and(
  z.object({
    qtyQ4: z
      .number("Informe a quantidade do lote")
      .int("Quantidade inválida")
      .min(1, "Quantidade do lote deve ser maior que zero")
      .max(MAX_STOCK_QTY_Q4, "Quantidade acima do limite"),
  }),
);
export type LotQtyInput = z.output<typeof lotQtyInput>;

/** Escolha manual de lotes numa saída: lote e quantidade (soma = quantidade da saída). */
export const lotTakeInput = z.object({
  lotId: ulidParam,
  qtyQ4: z
    .number("Informe a quantidade do lote")
    .int("Quantidade inválida")
    .min(1, "Quantidade do lote deve ser maior que zero")
    .max(MAX_STOCK_QTY_Q4, "Quantidade acima do limite"),
});
export const lotTakes = z
  .array(lotTakeInput)
  .max(50, "No máximo 50 lotes por saída")
  .nullish()
  .transform((v) => (v && v.length ? v : null));
export type LotTakeInput = z.output<typeof lotTakeInput>;

export const LOT_STATUS_FILTERS = ["all", "expired", "expiring", "valid", "no_expiry"] as const;

/** Lista de lotes: por padrão só os com saldo; `status` filtra pela validade (`days` = janela do "vencendo"). */
export const lotListQuery = listQuery.extend({
  productId: ulidParam.optional(),
  warehouseId: ulidParam.optional(),
  status: z.enum(LOT_STATUS_FILTERS, "Situação inválida").default("all"),
  days: z.coerce.number().int().min(1).max(365).default(30),
  withStock: z.stringbool({ error: "Use true ou false" }).default(true),
});
export type LotListQuery = z.output<typeof lotListQuery>;

export type LotListItem = {
  id: string;
  productId: string;
  productName: string;
  productSku: string | null;
  unit: string;
  code: string;
  manufacturedAt: string | null;
  expiresAt: string | null;
  status: LotStatus;
  /** Dias até a validade (negativo = vencido); null sem validade. */
  daysToExpire: number | null;
  /** Saldo do lote no depósito filtrado, ou somando os depósitos. */
  physicalQ4: number;
};

/** Lotes do produto por depósito (painel de estoque): "Sem lote" = físico − Σ lotes. */
export type ProductLotsView = {
  productId: string;
  batchControl: boolean;
  warehouses: {
    warehouseId: string;
    warehouseName: string;
    physicalQ4: number;
    semLoteQ4: number;
    lots: {
      lotId: string;
      code: string;
      manufacturedAt: string | null;
      expiresAt: string | null;
      status: LotStatus;
      physicalQ4: number;
    }[];
  }[];
};

/** Lotes do movimento (extrato). */
export type MovementLotView = { lotId: string; code: string; deltaQ4: number };
